import type { Page } from '@/domain/model/Page';
import type { $Fetch } from 'ofetch';
import type {
  ContentProviderGateway,
  ExternalContentGateway,
  UnidadeGateway,
} from '@/application/port/out/ConteudoExternoGateways';
import type {
  CatalogItem,
  ContentProvider,
  ExternalContent,
  ExternalContentFilter,
  Unidade,
  UpsertContentProviderCommand,
  UpsertExternalContentCommand,
  UpsertUnidadeCommand,
} from '@/domain/model/ConteudoExterno';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const PROVIDERS = '/api/v1/content-providers';
const UNIDADES = '/api/v1/unidades';
const EXTERNAL = '/api/v1/external-contents';

export class HttpContentProviderGateway implements ContentProviderGateway {
  constructor(private readonly client: $Fetch) {}

  async list(): Promise<ContentProvider[]> {
    const res = await this.client<ApiResponse<ContentProvider[]>>(PROVIDERS, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
  async create(cmd: UpsertContentProviderCommand): Promise<ContentProvider> {
    const res = await this.client<ApiResponse<ContentProvider>>(PROVIDERS, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async update(id: string, cmd: UpsertContentProviderCommand): Promise<ContentProvider> {
    const res = await this.client<ApiResponse<ContentProvider>>(`${PROVIDERS}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async archive(id: string): Promise<void> {
    // Backend responde 204 sem corpo (soft-delete) — sem envelope pra validar.
    await this.client<void>(`${PROVIDERS}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
  async browseCatalog(id: string): Promise<CatalogItem[]> {
    const res = await this.client<ApiResponse<CatalogItem[]>>(`${PROVIDERS}/${encodeURIComponent(id)}/catalog`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
}

export class HttpUnidadeGateway implements UnidadeGateway {
  constructor(private readonly client: $Fetch) {}

  async list(params: { page?: number; size?: number }): Promise<Page<Unidade>> {
    const res = await this.client<ApiResponse<Page<Unidade>>>(UNIDADES, { method: 'GET', params });
    assertApiSuccess(res);
    return res.data;
  }
  async create(cmd: UpsertUnidadeCommand): Promise<Unidade> {
    const res = await this.client<ApiResponse<Unidade>>(UNIDADES, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async update(id: string, cmd: UpsertUnidadeCommand): Promise<Unidade> {
    const res = await this.client<ApiResponse<Unidade>>(`${UNIDADES}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async archive(id: string): Promise<void> {
    await this.client<void>(`${UNIDADES}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

export class HttpExternalContentGateway implements ExternalContentGateway {
  constructor(private readonly client: $Fetch) {}

  async list(filter?: ExternalContentFilter): Promise<ExternalContent[]> {
    const params: Record<string, string> = {};
    if (filter?.kind) params['kind'] = filter.kind;
    if (filter?.disciplineId) params['disciplineId'] = filter.disciplineId;
    if (filter?.providerId) params['providerId'] = filter.providerId;
    const res = await this.client<ApiResponse<ExternalContent[]>>(EXTERNAL, { method: 'GET', params });
    assertApiSuccess(res);
    return res.data;
  }
  async create(cmd: UpsertExternalContentCommand): Promise<ExternalContent> {
    const res = await this.client<ApiResponse<ExternalContent>>(EXTERNAL, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async update(id: string, cmd: UpsertExternalContentCommand): Promise<ExternalContent> {
    const res = await this.client<ApiResponse<ExternalContent>>(`${EXTERNAL}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async archive(id: string): Promise<void> {
    await this.client<void>(`${EXTERNAL}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
